import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type {
  FramingSpacingOriginPoint,
  OpeningLayoutBounds,
  PerimeterLayoutBounds,
} from "building-types/framing/framingLayoutTypes";
import type { FramingBoundaryWithMetadata, FramingConfig } from "building-types/framing/framingMetaDataTypes";
import type { FramingProfileDescriptor } from "building-types/framing/framingProfileDescriptorTypes";
import type {
  DoorBottomPlateGap,
  GeneratedWallOpeningMember,
  OpeningFramingExtents,
  ValidatedWallOpening,
  WallOpeningFramingContext,
  WallOpeningFramingMemberOptions,
  WallOpeningValidationOptions,
  WallOpeningValidationResult,
} from "building-types/framing/wallFramingOpeningTypes";
import { WallOpeningFramingMode } from "building-types/framing/wallFramingOpeningTypes";
import type { WallOpeningFramingConfigGroup } from "building-types/framing/wallFramingTypes";
import { MIN_FRAMING_STUD_LENGTH, WallFramingType } from "building-types/framing/wallFramingTypes";
import type { Table } from "common/data/table";
import uid from "common/utils/uid";
import { getFramingProfileDescriptorById } from "doc/utils/lumberTools";
import { classifyContainmentPolyLine2PolyLine2Fast, PolyLine2Containment } from "geom/containment";
import { DIST_EPSILON } from "geom/core";
import { polyLine2EdgeCount } from "geom/polyLine2";
import { polyLine2ByTwoPoints } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import {
  computeProjectedDistance,
  generateFramingSpacingOriginPoints,
  layoutPointFromPerpParallel,
  spacingOriginRailPerpDistance,
} from "../framingLayoutUtils";
import {
  classifyBoundaryEdgeType,
  openingHeaderPerpSpan,
  openingHeaderSillRailParallel,
  openingHeaderSillVerticalExtent,
  openingHeaderVerticalExtent,
  openingSillPerpSpan,
  plateFlatVerticalThickness,
  roughOpeningEndPerp,
  roughOpeningStartPerp,
} from "./wallFramingUtils";
import { makeOpeningVerticalMemberRail } from "./wallFramingVerticalMemberExtents";

export function classifyOpeningPerimeterOverlap(
  openingBounds: OpeningLayoutBounds,
  perimeterBounds: PerimeterLayoutBounds,
  bottomPlateStackParallel: number,
): WallOpeningFramingMode | "invalid" {
  const overlapsBottom =
    openingBounds.bottomParallel <= perimeterBounds.bottomParallel + bottomPlateStackParallel + DIST_EPSILON;
  const overlapsTop = openingBounds.topParallel >= perimeterBounds.topParallel - DIST_EPSILON;
  const overlapsLeft = openingBounds.startPerp <= perimeterBounds.startPerp + DIST_EPSILON;
  const overlapsRight = openingBounds.endPerp >= perimeterBounds.endPerp - DIST_EPSILON;

  if (overlapsTop || overlapsLeft || overlapsRight) {
    return "invalid";
  }
  if (overlapsBottom) {
    return WallOpeningFramingMode.DOOR;
  }
  return WallOpeningFramingMode.WINDOW;
}

function overlapErrorDescription(
  openingIndex: number,
  openingBounds: OpeningLayoutBounds,
  perimeterBounds: PerimeterLayoutBounds,
): string {
  const parts: string[] = [];
  if (openingBounds.topParallel >= perimeterBounds.topParallel - DIST_EPSILON) {
    parts.push("top");
  }
  if (openingBounds.startPerp <= perimeterBounds.startPerp + DIST_EPSILON) {
    parts.push("left");
  }
  if (openingBounds.endPerp >= perimeterBounds.endPerp - DIST_EPSILON) {
    parts.push("right");
  }
  const edges = parts.length > 0 ? parts.join("/") : "top/left/right";
  return `Opening ${openingIndex} overlaps ${edges} perimeter edge; opening framing skipped`;
}

function outsideFramingErrorDescription(openingIndex: number): string {
  return `Opening ${openingIndex} is outside the wall framing perimeter; opening framing skipped`;
}

/**
 * Rectangular AABB checks miss gable and other non-rectangular perimeters. Require the opening
 * polygon to lie inside the framing perimeter unless it is a door overlapping only the bottom plate.
 */
export function isOpeningPolylineInsideFramingPerimeter(
  openingPolyline: PolyLine2,
  perimeterPolyline: PolyLine2,
  aabbClassification: WallOpeningFramingMode,
): boolean {
  const containment = classifyContainmentPolyLine2PolyLine2Fast(openingPolyline, perimeterPolyline);
  if (containment === PolyLine2Containment.INSIDE) {
    return true;
  }
  if (aabbClassification === WallOpeningFramingMode.DOOR) {
    return true;
  }
  return false;
}

export function validateWallOpenings(
  openingBoundsList: OpeningLayoutBounds[],
  perimeterBounds: PerimeterLayoutBounds,
  plateThickness: number,
  bottomPlateCount: number,
  options?: WallOpeningValidationOptions,
): WallOpeningValidationResult {
  const bottomPlateStackParallel = plateThickness * bottomPlateCount;
  const validOpenings: ValidatedWallOpening[] = [];
  const errors: string[] = [];
  const doorBottomPlateGaps: DoorBottomPlateGap[] = [];

  for (const bounds of openingBoundsList) {
    const classification = classifyOpeningPerimeterOverlap(bounds, perimeterBounds, bottomPlateStackParallel);
    if (classification === "invalid") {
      errors.push(overlapErrorDescription(bounds.openingIndex, bounds, perimeterBounds));
      continue;
    }

    const openingPolyline = options?.openingPolylines?.[bounds.openingIndex];
    if (
      options?.perimeterPolyline &&
      openingPolyline &&
      !isOpeningPolylineInsideFramingPerimeter(openingPolyline, options.perimeterPolyline, classification)
    ) {
      errors.push(outsideFramingErrorDescription(bounds.openingIndex));
      continue;
    }

    validOpenings.push({
      openingIndex: bounds.openingIndex,
      bounds,
      mode: classification,
    });
    if (classification === WallOpeningFramingMode.DOOR) {
      doorBottomPlateGaps.push({ startPerp: bounds.startPerp, endPerp: bounds.endPerp });
    }
  }

  return { validOpenings, errors, doorBottomPlateGaps };
}

export function extendedOpeningGapPerpRange(
  bounds: OpeningLayoutBounds,
  jackCount: number,
  kingCount: number,
  studThickness: number,
): { minPerp: number; maxPerp: number } {
  const jackOffset = jackCount * studThickness;
  const kingOffset = kingCount * studThickness;
  return {
    minPerp: bounds.startPerp - jackOffset - kingOffset,
    maxPerp: bounds.endPerp + jackOffset + kingOffset,
  };
}

export function resolveWallOpeningProfile(
  config: Pick<FramingConfig, "framingComponentId" | "disabled"> | undefined,
  defaultProfile: FramingProfileDescriptor,
  framingComponents: Table<FramingComponentEntity>,
): FramingProfileDescriptor {
  if (!config?.framingComponentId) {
    return defaultProfile;
  }
  return getFramingProfileDescriptorById(framingComponents, config.framingComponentId) ?? defaultProfile;
}

function makeVerticalRail(
  ctx: WallOpeningFramingContext,
  perpDistance: number,
  bottomParallel: number,
  topParallel: number,
): PolyLine2 {
  const [x0, y0] = layoutPointFromPerpParallel(
    perpDistance,
    bottomParallel,
    ctx.planeOrigin,
    ctx.perpDirection,
    ctx.framingDirection,
  );
  const [x1, y1] = layoutPointFromPerpParallel(
    perpDistance,
    topParallel,
    ctx.planeOrigin,
    ctx.perpDirection,
    ctx.framingDirection,
  );
  return polyLine2ByTwoPoints(x0, y0, x1, y1);
}

function pushSlopedVerticalOpeningMember(
  members: GeneratedWallOpeningMember[],
  ctx: WallOpeningFramingContext,
  perpDistance: number,
  minBottomParallel: number,
  profileDescriptor: FramingProfileDescriptor,
  framingType: WallFramingType.KING_STUD | WallFramingType.TOP_CRIPPLE_STUD,
): void {
  const rail = makeOpeningVerticalMemberRail(ctx, perpDistance, minBottomParallel);
  if (!rail) {
    return;
  }
  members.push({
    polyline: rail.polyline,
    profileDescriptor,
    framingType,
    depthStackIndex: 0,
    startHit: rail.startHit,
    endHit: rail.endHit,
  });
}

function makeHorizontalRail(
  ctx: WallOpeningFramingContext,
  startPerp: number,
  endPerp: number,
  parallelDistance: number,
): PolyLine2 {
  const [x0, y0] = layoutPointFromPerpParallel(
    startPerp,
    parallelDistance,
    ctx.planeOrigin,
    ctx.perpDirection,
    ctx.framingDirection,
  );
  const [x1, y1] = layoutPointFromPerpParallel(
    endPerp,
    parallelDistance,
    ctx.planeOrigin,
    ctx.perpDirection,
    ctx.framingDirection,
  );
  return polyLine2ByTwoPoints(x0, y0, x1, y1);
}

function isConfigEnabled(config: Pick<FramingConfig, "disabled"> | undefined): boolean {
  return config !== undefined && !config.disabled;
}

export function configCount(config: FramingConfig | undefined, defaultCount: number = 1): number {
  if (!isConfigEnabled(config)) {
    return 0;
  }
  return Math.max(1, config?.count ?? defaultCount);
}

export function headerSillCountFromConfig(configGroup: WallOpeningFramingConfigGroup): number {
  const config = configGroup.headerSillConfig;
  if (config === undefined) {
    return 0;
  }
  return configCount(config);
}

export function computeOpeningFramingExtents(
  opening: ValidatedWallOpening,
  configGroup: WallOpeningFramingConfigGroup,
  defaultProfile: FramingProfileDescriptor,
  framingComponents: Table<FramingComponentEntity>,
  plateThickness: number,
): OpeningFramingExtents {
  const jackCount = configCount(configGroup.jackStudConfig);
  const kingCount = configCount(configGroup.kingStudConfig);
  const headerCount = configCount(configGroup.headerConfig);
  const sillCount = opening.mode === WallOpeningFramingMode.WINDOW ? configCount(configGroup.sillPlateConfig) : 0;
  const studThickness = defaultProfile.height;
  const jackOffset = jackCount * studThickness;
  const kingOffset = kingCount * studThickness;
  const headerProfile = resolveWallOpeningProfile(configGroup.headerConfig, defaultProfile, framingComponents);
  const headerSillCount = headerSillCountFromConfig(configGroup);
  const headerSillProfile = resolveWallOpeningProfile(configGroup.headerSillConfig, defaultProfile, framingComponents);
  const headerSillStackHeight = openingHeaderSillVerticalExtent(headerSillCount, headerSillProfile);
  const headerStackHeight = openingHeaderVerticalExtent(headerCount, headerProfile);

  return {
    openingIndex: opening.openingIndex,
    startPerp: opening.bounds.startPerp - jackOffset - kingOffset,
    endPerp: opening.bounds.endPerp + jackOffset + kingOffset,
    headerTopParallel: opening.bounds.topParallel + headerSillStackHeight + headerStackHeight,
    sillBottomParallel: opening.bounds.bottomParallel - sillCount * plateThickness,
  };
}

export function framingExtentsPerpOverlap(
  a: OpeningFramingExtents,
  b: OpeningFramingExtents,
): { readonly minPerp: number; readonly maxPerp: number } | undefined {
  const minPerp = Math.max(a.startPerp, b.startPerp);
  const maxPerp = Math.min(a.endPerp, b.endPerp);
  if (minPerp >= maxPerp - DIST_EPSILON) {
    return undefined;
  }
  return { minPerp, maxPerp };
}

export function openingSillBottomParallel(
  opening: ValidatedWallOpening,
  configGroup: WallOpeningFramingConfigGroup,
  plateThickness: number,
): number | undefined {
  if (opening.mode !== WallOpeningFramingMode.WINDOW) {
    return undefined;
  }
  const sillCount = configCount(configGroup.sillPlateConfig);
  if (sillCount <= 0) {
    return undefined;
  }
  return opening.bounds.bottomParallel - sillCount * plateThickness;
}

export function verticallyAdjacentForHeaderSillMerge(
  lower: ValidatedWallOpening,
  upper: ValidatedWallOpening,
  lowerExtents: OpeningFramingExtents,
  upperExtents: OpeningFramingExtents,
  mergeClearance: number,
): boolean {
  if (lower.bounds.topParallel > upper.bounds.bottomParallel + DIST_EPSILON) {
    return false;
  }
  const gap = upperExtents.sillBottomParallel - lowerExtents.headerTopParallel;
  return gap <= mergeClearance + DIST_EPSILON;
}

export function generateWallOpeningFramingMembers(
  opening: ValidatedWallOpening,
  configGroup: WallOpeningFramingConfigGroup,
  ctx: WallOpeningFramingContext,
  framingComponents: Table<FramingComponentEntity>,
  defaultProfile: FramingProfileDescriptor,
  memberOptions?: WallOpeningFramingMemberOptions,
): GeneratedWallOpeningMember[] {
  const includeKings = memberOptions?.includeKings ?? true;
  const includeJacks = memberOptions?.includeJacks ?? true;
  const includeHeaders = memberOptions?.includeHeaders ?? true;
  const includeHeaderSills = memberOptions?.includeHeaderSills ?? true;
  const includeSills = memberOptions?.includeSills ?? true;
  const includeCripples = memberOptions?.includeCripples ?? true;
  const members: GeneratedWallOpeningMember[] = [];
  const { bounds, mode } = opening;

  const studProfile = defaultProfile;
  const studThickness = studProfile.height;
  const plateThickness = studProfile.height;

  const kingCount = configCount(configGroup.kingStudConfig);
  const jackCount = configCount(configGroup.jackStudConfig);
  const headerCount = configCount(configGroup.headerConfig);
  const sillCount = mode === WallOpeningFramingMode.WINDOW ? configCount(configGroup.sillPlateConfig) : 0;

  const kingProfile = resolveWallOpeningProfile(configGroup.kingStudConfig, defaultProfile, framingComponents);
  const jackProfile = resolveWallOpeningProfile(configGroup.jackStudConfig, defaultProfile, framingComponents);
  const headerProfile = resolveWallOpeningProfile(configGroup.headerConfig, defaultProfile, framingComponents);
  const headerSillCount = headerSillCountFromConfig(configGroup);
  const headerSillProfile = resolveWallOpeningProfile(configGroup.headerSillConfig, defaultProfile, framingComponents);
  const sillProfile = resolveWallOpeningProfile(configGroup.sillPlateConfig, defaultProfile, framingComponents);
  const topCrippleProfile = resolveWallOpeningProfile(
    configGroup.topCrippleStudConfig,
    defaultProfile,
    framingComponents,
  );

  const jackOffset = jackCount * studThickness;
  const assemblyBounds = memberOptions?.assemblyLayoutBounds;
  const headerBounds = assemblyBounds ?? bounds;
  const { startPerp: headerStartPerp, endPerp: headerEndPerp } = openingHeaderPerpSpan(headerBounds, jackOffset);
  const sillBounds = assemblyBounds ?? bounds;
  const { startPerp: sillStartPerp, endPerp: sillEndPerp } = openingSillPerpSpan(sillBounds);
  const headerSillStackHeight = openingHeaderSillVerticalExtent(headerSillCount, headerSillProfile);
  const headerSillPlateThickness = plateFlatVerticalThickness(headerSillProfile);
  const headerStackHeight = openingHeaderVerticalExtent(headerCount, headerProfile);
  const headerBaseParallel = bounds.topParallel + headerSillStackHeight;

  const jackBounds = assemblyBounds ?? bounds;
  const roStart = roughOpeningStartPerp(jackBounds);
  const roEnd = roughOpeningEndPerp(jackBounds);
  const jackBottomParallel = memberOptions?.jackBottomParallel ?? ctx.studBottomParallel;
  const jackTopParallel = memberOptions?.jackTopParallel ?? bounds.topParallel;

  if (includeKings) {
    for (let k = 0; k < kingCount; k++) {
      const leftPerp = roStart + (k + 1) * studThickness + jackOffset;
      const rightPerp = roEnd - jackOffset - k * studThickness;
      pushSlopedVerticalOpeningMember(
        members,
        ctx,
        leftPerp,
        ctx.studBottomParallel,
        kingProfile,
        WallFramingType.KING_STUD,
      );
      pushSlopedVerticalOpeningMember(
        members,
        ctx,
        rightPerp,
        ctx.studBottomParallel,
        kingProfile,
        WallFramingType.KING_STUD,
      );
    }
  }

  if (includeJacks) {
    for (let k = 0; k < jackCount; k++) {
      // k=0 is the inner jack (opening-adjacent); higher k stacks outward toward king studs.
      const leftPerp = roStart + jackOffset - studThickness * (jackCount - 1 - k);
      const rightPerp = roEnd - k * studThickness;
      members.push({
        polyline: makeVerticalRail(ctx, leftPerp, jackBottomParallel, jackTopParallel),
        profileDescriptor: jackProfile,
        framingType: WallFramingType.JACK_STUD,
        depthStackIndex: 0,
      });
      members.push({
        polyline: makeVerticalRail(ctx, rightPerp, jackBottomParallel, jackTopParallel),
        profileDescriptor: jackProfile,
        framingType: WallFramingType.JACK_STUD,
        depthStackIndex: 0,
      });
    }
  }

  if (includeHeaderSills && headerSillCount > 0) {
    for (let s = 0; s < headerSillCount; s++) {
      members.push({
        polyline: makeHorizontalRail(
          ctx,
          headerStartPerp,
          headerEndPerp,
          openingHeaderSillRailParallel(bounds.topParallel, s, headerSillPlateThickness),
        ),
        profileDescriptor: headerSillProfile,
        framingType: WallFramingType.TOP_SILL_PLATE,
        depthStackIndex: 0,
      });
    }
  }

  if (includeHeaders) {
    // Tag every ply of this header with one shared id so downstream consolidation can regroup the
    // depth-stacked members into a single header holding all plies as rails (like the legacy pipeline).
    const headerGroupId = uid();
    for (let h = 0; h < headerCount; h++) {
      members.push({
        polyline: makeHorizontalRail(ctx, headerStartPerp, headerEndPerp, headerBaseParallel),
        profileDescriptor: headerProfile,
        framingType: WallFramingType.HEADER,
        depthStackIndex: h,
        headerGroupId,
      });
    }
  }

  if (includeSills && sillCount > 0 && bounds.bottomParallel > ctx.studBottomParallel + DIST_EPSILON) {
    const sillBaseParallel = bounds.bottomParallel - sillCount * plateThickness;
    for (let s = 0; s < sillCount; s++) {
      members.push({
        polyline: makeHorizontalRail(ctx, sillStartPerp, sillEndPerp, sillBaseParallel + s * plateThickness),
        profileDescriptor: sillProfile,
        framingType: WallFramingType.BOTTOM_SILL_PLATE,
        depthStackIndex: 0,
      });
    }
  }

  const spacingOrigins = generateFramingSpacingOriginPoints(
    ctx.sortedVertices,
    ctx.spacing,
    ctx.planeOrigin,
    ctx.framingDirection,
    ctx.perpDirection,
    true,
    true,
  );

  // Bottom cripples (field and jamb ends) are produced by the band/cluster path in
  // `addWallCrippleMembersInPerpSpan`; this generator only emits the top cripples above the header.
  const minCripplePerp = roEnd;
  const maxCripplePerp = roStart;
  const kingPerps = openingKingPerpPositions(roStart, roEnd, studThickness, kingCount, jackOffset);

  if (includeCripples && isConfigEnabled(configGroup.topCrippleStudConfig)) {
    const topCrippleBottom = bounds.topParallel + headerSillStackHeight + headerStackHeight;
    addCrippleMembers(
      members,
      spacingOrigins,
      minCripplePerp,
      maxCripplePerp,
      kingPerps,
      studThickness,
      topCrippleBottom,
      ctx.studTopParallel,
      ctx,
      topCrippleProfile,
      WallFramingType.TOP_CRIPPLE_STUD,
    );
  }

  return members;
}

export function bandKingJackPerpPositions(
  combinedRoughOpeningBounds: OpeningLayoutBounds,
  configGroup: WallOpeningFramingConfigGroup,
  studThickness: number,
): number[] {
  const roStart = roughOpeningStartPerp(combinedRoughOpeningBounds);
  const roEnd = roughOpeningEndPerp(combinedRoughOpeningBounds);
  const jackCount = configCount(configGroup.jackStudConfig);
  const kingCount = configCount(configGroup.kingStudConfig);
  const jackOffset = jackCount * studThickness;
  return openingKingJackPerpPositions(roStart, roEnd, studThickness, jackCount, kingCount, jackOffset);
}

export function addWallCrippleMembersInPerpSpan(
  members: GeneratedWallOpeningMember[],
  minCripplePerp: number,
  maxCripplePerp: number,
  bottomParallel: number,
  topParallel: number,
  configGroup: WallOpeningFramingConfigGroup,
  ctx: WallOpeningFramingContext,
  framingComponents: Table<FramingComponentEntity>,
  defaultProfile: FramingProfileDescriptor,
  framingType: WallFramingType.BOTTOM_CRIPPLE_STUD | WallFramingType.TOP_CRIPPLE_STUD,
  obstructingStudPerpsOverride?: readonly number[],
): void {
  const crippleConfig =
    framingType === WallFramingType.BOTTOM_CRIPPLE_STUD
      ? configGroup.bottomCrippleStudConfig
      : configGroup.topCrippleStudConfig;

  const fieldCripplesEnabled = isConfigEnabled(crippleConfig);
  const hasCrippleStudEnds =
    framingType === WallFramingType.BOTTOM_CRIPPLE_STUD && configGroup.hasCrippleStudEnds === true;
  if (!fieldCripplesEnabled && !hasCrippleStudEnds) {
    return;
  }

  const studThickness = defaultProfile.height;
  const jackCount = configCount(configGroup.jackStudConfig);
  const kingCount = configCount(configGroup.kingStudConfig);
  const jackOffset = jackCount * studThickness;
  const roStart = maxCripplePerp;
  const roEnd = minCripplePerp;
  const crippleProfile = resolveWallOpeningProfile(crippleConfig, defaultProfile, framingComponents);
  const spacingOrigins = generateFramingSpacingOriginPoints(
    ctx.sortedVertices,
    ctx.spacing,
    ctx.planeOrigin,
    ctx.framingDirection,
    ctx.perpDirection,
    true,
    true,
  );
  const obstructingStudPerps = resolveCrippleObstructingStudPerps(
    framingType,
    roStart,
    roEnd,
    studThickness,
    jackCount,
    kingCount,
    jackOffset,
    obstructingStudPerpsOverride,
  );

  if (fieldCripplesEnabled) {
    addCrippleMembers(
      members,
      spacingOrigins,
      minCripplePerp,
      maxCripplePerp,
      obstructingStudPerps,
      studThickness,
      bottomParallel,
      topParallel,
      ctx,
      crippleProfile,
      framingType,
    );
  }

  if (hasCrippleStudEnds) {
    // Center each jamb cripple half a stud inside the rough-opening edge so its outer face sits
    // flush with the jamb (and the inside face of the jack stud).
    addBottomCrippleStudEndsRemovingOverlaps(
      members,
      roStart - studThickness / 2,
      roEnd + studThickness / 2,
      studThickness,
      bottomParallel,
      topParallel,
      ctx,
      crippleProfile,
    );
  }
}

function resolveCrippleObstructingStudPerps(
  framingType: WallFramingType.BOTTOM_CRIPPLE_STUD | WallFramingType.TOP_CRIPPLE_STUD,
  roStart: number,
  roEnd: number,
  studThickness: number,
  jackCount: number,
  kingCount: number,
  jackOffset: number,
  obstructingStudPerpsOverride: readonly number[] | undefined,
): readonly number[] {
  if (framingType === WallFramingType.TOP_CRIPPLE_STUD) {
    return openingKingPerpPositions(roStart, roEnd, studThickness, kingCount, jackOffset);
  }
  if (obstructingStudPerpsOverride) {
    return obstructingStudPerpsOverride;
  }
  return openingKingJackPerpPositions(roStart, roEnd, studThickness, jackCount, kingCount, jackOffset);
}

/** King stud centerlines; jacks are omitted because they do not run through the top cripple zone. */
export function openingKingPerpPositions(
  roStart: number,
  roEnd: number,
  studThickness: number,
  kingCount: number,
  jackOffset: number,
): number[] {
  const perps: number[] = [];
  for (let k = 0; k < kingCount; k++) {
    perps.push(roStart + (k + 1) * studThickness + jackOffset);
    perps.push(roEnd - jackOffset - k * studThickness);
  }
  return perps;
}

export function openingKingJackPerpPositions(
  roStart: number,
  roEnd: number,
  studThickness: number,
  jackCount: number,
  kingCount: number,
  jackOffset: number,
): number[] {
  const perps = openingKingPerpPositions(roStart, roEnd, studThickness, kingCount, jackOffset);
  for (let k = 0; k < jackCount; k++) {
    perps.push(roStart + jackOffset - studThickness * (jackCount - 1 - k));
    perps.push(roEnd - k * studThickness);
  }
  return perps;
}

/**
 * Top cripples only skip king columns (jacks end below the header). Use half-stud tolerance so
 * adjacent jack centerlines one stud thickness from kings are not treated as obstructed.
 */
export function spacingOriginObstructsStudPerp(
  originPerp: number,
  studPerp: number,
  studThickness: number,
  framingType: WallFramingType.BOTTOM_CRIPPLE_STUD | WallFramingType.TOP_CRIPPLE_STUD,
): boolean {
  const tolerance =
    framingType === WallFramingType.TOP_CRIPPLE_STUD ? studThickness / 2 + DIST_EPSILON : studThickness + DIST_EPSILON;
  return Math.abs(originPerp - studPerp) <= tolerance;
}

/**
 * True when `member` is a bottom cripple whose footprint overlaps a jamb-end cripple centered at
 * `jambCenterPerp` and spanning `[bottomParallel, topParallel]`. The parallel (vertical) check keeps
 * removals scoped to the same band so an inter-opening end never deletes a cripple at the same perp
 * in a different vertical region.
 */
function bottomCrippleOverlapsJambEnd(
  member: GeneratedWallOpeningMember,
  jambCenterPerp: number,
  studThickness: number,
  bottomParallel: number,
  topParallel: number,
  ctx: WallOpeningFramingContext,
): boolean {
  if (member.framingType !== WallFramingType.BOTTOM_CRIPPLE_STUD) {
    return false;
  }
  const rail = member.polyline;
  const railPerp = computeProjectedDistance([rail[0], rail[1]], ctx.planeOrigin, ctx.perpDirection);
  const memberCenterPerp = railPerp + ctx.spacingSign * ctx.studHalfThickness;
  if (Math.abs(memberCenterPerp - jambCenterPerp) >= studThickness - DIST_EPSILON) {
    return false;
  }
  const railEndParallelA = computeProjectedDistance([rail[0], rail[1]], ctx.planeOrigin, ctx.framingDirection);
  const railEndParallelB = computeProjectedDistance([rail[2], rail[3]], ctx.planeOrigin, ctx.framingDirection);
  const minParallel = Math.min(railEndParallelA, railEndParallelB);
  const maxParallel = Math.max(railEndParallelA, railEndParallelB);
  return minParallel < topParallel - DIST_EPSILON && maxParallel > bottomParallel + DIST_EPSILON;
}

/**
 * Adds a bottom cripple flush inside each jamb (centers passed as `*JambCenterPerp`). A requested
 * jamb cripple takes precedence over the regular-spacing field grid: any field bottom cripple that
 * would overlap a jamb end is removed so studs are not doubled up at the opening end.
 */
function addBottomCrippleStudEndsRemovingOverlaps(
  members: GeneratedWallOpeningMember[],
  leftJambCenterPerp: number,
  rightJambCenterPerp: number,
  studThickness: number,
  bottomParallel: number,
  topParallel: number,
  ctx: WallOpeningFramingContext,
  profileDescriptor: FramingProfileDescriptor,
): void {
  if (topParallel - bottomParallel < MIN_FRAMING_STUD_LENGTH) {
    return;
  }

  for (const jambCenterPerp of [leftJambCenterPerp, rightJambCenterPerp]) {
    for (let i = members.length - 1; i >= 0; i--) {
      if (bottomCrippleOverlapsJambEnd(members[i], jambCenterPerp, studThickness, bottomParallel, topParallel, ctx)) {
        members.splice(i, 1);
      }
    }
    members.push({
      polyline: makeVerticalRail(
        ctx,
        spacingOriginRailPerpDistance(jambCenterPerp, ctx.spacingSign, ctx.studHalfThickness),
        bottomParallel,
        topParallel,
      ),
      profileDescriptor,
      framingType: WallFramingType.BOTTOM_CRIPPLE_STUD,
      depthStackIndex: 0,
    });
  }
}

function addCrippleMembers(
  members: GeneratedWallOpeningMember[],
  spacingOrigins: FramingSpacingOriginPoint[],
  minCripplePerp: number,
  maxCripplePerp: number,
  obstructingStudPerps: readonly number[],
  studThickness: number,
  bottomParallel: number,
  topParallel: number,
  ctx: WallOpeningFramingContext,
  profileDescriptor: FramingProfileDescriptor,
  framingType: WallFramingType.BOTTOM_CRIPPLE_STUD | WallFramingType.TOP_CRIPPLE_STUD,
): void {
  if (topParallel - bottomParallel < MIN_FRAMING_STUD_LENGTH) {
    return;
  }

  for (const origin of spacingOrigins) {
    if (origin.perpDistance < minCripplePerp - DIST_EPSILON || origin.perpDistance > maxCripplePerp + DIST_EPSILON) {
      continue;
    }
    if (
      obstructingStudPerps.some((studPerp) =>
        spacingOriginObstructsStudPerp(origin.perpDistance, studPerp, studThickness, framingType),
      )
    ) {
      continue;
    }

    if (framingType === WallFramingType.TOP_CRIPPLE_STUD) {
      const railPerp = spacingOriginRailPerpDistance(origin.perpDistance, ctx.spacingSign, ctx.studHalfThickness);
      pushSlopedVerticalOpeningMember(
        members,
        ctx,
        railPerp,
        bottomParallel,
        profileDescriptor,
        WallFramingType.TOP_CRIPPLE_STUD,
      );
      continue;
    }

    members.push({
      polyline: makeVerticalRail(
        ctx,
        spacingOriginRailPerpDistance(origin.perpDistance, ctx.spacingSign, ctx.studHalfThickness),
        bottomParallel,
        topParallel,
      ),
      profileDescriptor,
      framingType,
      depthStackIndex: 0,
    });
  }
}

export function splitBottomPlateEdgeAtDoorGaps(
  edgeRail: PolyLine2,
  doorGaps: DoorBottomPlateGap[],
  planeOrigin: Vec2,
  perpDirection: Vec2,
  framingDirection: Vec2,
): PolyLine2[] {
  if (doorGaps.length === 0) {
    return [edgeRail];
  }

  const x0 = edgeRail[0];
  const y0 = edgeRail[1];
  const x1 = edgeRail[2];
  const y1 = edgeRail[3];

  const edgeStartPerp = computeProjectedDistance([x0, y0], planeOrigin, perpDirection);
  const edgeEndPerp = computeProjectedDistance([x1, y1], planeOrigin, perpDirection);
  const edgeRunsIncreasingPerp = edgeEndPerp >= edgeStartPerp - DIST_EPSILON;
  const edgeMinPerp = Math.min(edgeStartPerp, edgeEndPerp);
  const edgeMaxPerp = Math.max(edgeStartPerp, edgeEndPerp);
  const edgeLength = Math.abs(edgeEndPerp - edgeStartPerp);
  const edgeParallel =
    (computeProjectedDistance([x0, y0], planeOrigin, framingDirection) +
      computeProjectedDistance([x1, y1], planeOrigin, framingDirection)) /
    2;

  const perpAtEdgeT = (t: number): number => (edgeRunsIncreasingPerp ? edgeStartPerp + t : edgeStartPerp - t);

  const gapsOnEdge = [...doorGaps]
    .filter((gap) => gap.endPerp > edgeMinPerp + DIST_EPSILON && gap.startPerp < edgeMaxPerp - DIST_EPSILON)
    .map((gap) => {
      const gapStart = Math.max(gap.startPerp, edgeMinPerp);
      const gapEnd = Math.min(gap.endPerp, edgeMaxPerp);
      const gapT0 = edgeRunsIncreasingPerp ? gapStart - edgeStartPerp : edgeStartPerp - gapEnd;
      const gapT1 = edgeRunsIncreasingPerp ? gapEnd - edgeStartPerp : edgeStartPerp - gapStart;
      return { lowT: Math.min(gapT0, gapT1), highT: Math.max(gapT0, gapT1) };
    })
    .sort((a, b) => a.lowT - b.lowT);

  const segments: PolyLine2[] = [];
  let cursorT = 0;

  for (const gap of gapsOnEdge) {
    if (gap.lowT > cursorT + DIST_EPSILON) {
      segments.push(
        makeHorizontalRailFromLayout(
          planeOrigin,
          perpDirection,
          framingDirection,
          perpAtEdgeT(cursorT),
          perpAtEdgeT(gap.lowT),
          edgeParallel,
        ),
      );
    }
    cursorT = Math.max(cursorT, gap.highT);
  }

  if (edgeLength > cursorT + DIST_EPSILON) {
    segments.push(
      makeHorizontalRailFromLayout(
        planeOrigin,
        perpDirection,
        framingDirection,
        perpAtEdgeT(cursorT),
        perpAtEdgeT(edgeLength),
        edgeParallel,
      ),
    );
  }

  return segments.filter((seg) => {
    const dx = seg[2] - seg[0];
    const dy = seg[3] - seg[1];
    return Math.hypot(dx, dy) >= MIN_FRAMING_STUD_LENGTH;
  });
}

function makeHorizontalRailFromLayout(
  planeOrigin: Vec2,
  perpDirection: Vec2,
  framingDirection: Vec2,
  startPerp: number,
  endPerp: number,
  parallelDistance: number,
): PolyLine2 {
  const [x0, y0] = layoutPointFromPerpParallel(
    startPerp,
    parallelDistance,
    planeOrigin,
    perpDirection,
    framingDirection,
  );
  const [x1, y1] = layoutPointFromPerpParallel(endPerp, parallelDistance, planeOrigin, perpDirection, framingDirection);
  return polyLine2ByTwoPoints(x0, y0, x1, y1);
}

export function computeStudVerticalExtents(
  perimeterBounds: PerimeterLayoutBounds,
  plateThickness: number,
  bottomPlateCount: number,
  topPlateCount: number,
): { studBottomParallel: number; studTopParallel: number } {
  return {
    studBottomParallel: perimeterBounds.bottomParallel + bottomPlateCount * plateThickness,
    studTopParallel: perimeterBounds.topParallel - topPlateCount * plateThickness,
  };
}

export function countBottomAndTopPlates(perimeter: FramingBoundaryWithMetadata): {
  bottomPlateCount: number;
  topPlateCount: number;
} {
  let bottomPlateCount = 1;
  let topPlateCount = 1;

  for (let i = 0, l = polyLine2EdgeCount(perimeter.polyline); i < l; i++) {
    const edgeType = classifyBoundaryEdgeType(perimeter.polyline, i);
    const count = configCount(perimeter.edgeMetadata?.[i]?.framingConfig);
    if (edgeType === WallFramingType.BOTTOM_PLATE) {
      bottomPlateCount = Math.max(bottomPlateCount, count);
    }
    if (edgeType === WallFramingType.TOP_PLATE) {
      topPlateCount = Math.max(topPlateCount, count);
    }
  }

  return { bottomPlateCount, topPlateCount };
}
